import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import type { ConversationContext } from 'src/services/AIManagersService/conversationContext';

function isLong(value: string) {
    return value.length > 220 || value.split('\n').length > 3;
}

const clipped = {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
} as const;

export default function ConversationContextSummary({ context }: { context: ConversationContext }) {
    const [expanded, setExpanded] = useState(true);
    const [allFacts, setAllFacts] = useState(false);
    const [fullReason, setFullReason] = useState(false);
    const facts = allFacts ? context.facts : context.facts.slice(0, 3);
    const title =
        context.attention === 'review'
            ? 'Нужна проверка'
            : context.attention === 'handoff'
            ? 'Передан человеку'
            : 'По разговору';
    return (
        <Box
            component="section"
            aria-label="Контекст диалога"
            sx={{ pb: 2, mb: 1, borderBottom: '1px solid var(--lm-border)', overflowWrap: 'anywhere' }}
        >
            <Button
                aria-label={expanded ? 'Свернуть контекст' : 'Раскрыть контекст'}
                aria-expanded={expanded}
                aria-controls="conversation-context-body"
                onClick={() => setExpanded(!expanded)}
                fullWidth
                sx={{ p: 0, mb: expanded ? 1 : 0, justifyContent: 'space-between', color: 'text.primary' }}
            >
                <Typography
                    component="span"
                    variant="body2"
                    fontWeight={700}
                >
                    {title}
                </Typography>
                {expanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
            </Button>
            <Box
                id="conversation-context-body"
                hidden={!expanded}
            >
                {context.reason ? (
                    <Box mb={1.5}>
                        <Typography
                            variant="caption"
                            color="secondary.main"
                            component="p"
                            mb={0.5}
                        >
                            {context.reason.source === 'ai' ? 'Комментарий ИИ' : 'Комментарий к передаче'}
                        </Typography>
                        <Typography
                            id="conversation-reason"
                            variant="body2"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                ...(!fullReason && isLong(context.reason.text) ? clipped : {}),
                            }}
                        >
                            {context.reason.text}
                        </Typography>
                        {isLong(context.reason.text) && (
                            <Button
                                size="small"
                                aria-expanded={fullReason}
                                aria-controls="conversation-reason"
                                onClick={() => setFullReason(!fullReason)}
                                sx={{ px: 0 }}
                            >
                                {fullReason ? 'Свернуть комментарий' : 'Весь комментарий'}
                            </Button>
                        )}
                    </Box>
                ) : context.attention ? (
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mb={1.5}
                    >
                        Причина передачи не указана. Проверьте переписку.
                    </Typography>
                ) : null}
                {context.missingFacts.length > 0 && (
                    <Typography
                        variant="body2"
                        mb={1.5}
                    >
                        <Box
                            component="span"
                            color="secondary.main"
                        >
                            Нужно уточнить:{' '}
                        </Box>
                        {context.missingFacts.join(', ')}
                    </Typography>
                )}
                <Typography
                    variant="caption"
                    color="secondary.main"
                    component="p"
                    mb={1}
                >
                    Сведения о клиенте
                </Typography>
                {facts.length > 0 && (
                    <Stack
                        component="dl"
                        id="conversation-facts"
                        spacing={1.5}
                        m={0}
                    >
                        {facts.map((fact) => (
                            <Box key={fact.key}>
                                <Typography
                                    component="dt"
                                    variant="caption"
                                    color="secondary.main"
                                >
                                    {fact.label}
                                </Typography>
                                <Box
                                    component="dd"
                                    m={0}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            ...(!allFacts && isLong(fact.value) ? clipped : {}),
                                        }}
                                    >
                                        {fact.value}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="secondary.main"
                                    >
                                        {fact.source} ·{' '}
                                        {fact.verified === true
                                            ? 'Проверено'
                                            : fact.verified === false
                                            ? 'Не проверено'
                                            : 'Проверка не указана'}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )}
                {(context.facts.length > 3 || context.facts.some((fact) => isLong(fact.value))) && (
                    <Button
                        size="small"
                        aria-expanded={allFacts}
                        aria-controls="conversation-facts"
                        onClick={() => setAllFacts(!allFacts)}
                        sx={{ px: 0, mt: 1 }}
                    >
                        {allFacts ? 'Свернуть сведения' : `Все сведения · ${context.facts.length}`}
                    </Button>
                )}
                {(context.factsAvailability !== 'available' || !facts.length) && (
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={facts.length ? 1 : 0}
                    >
                        {context.factsAvailability === 'unavailable'
                            ? 'Сведения пока недоступны. Потребность можно уточнить в переписке.'
                            : context.factsAvailability === 'partial'
                            ? 'Часть сведений не удалось отобразить. Проверьте переписку.'
                            : 'Сведения ещё не собраны.'}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
